/**
 * titleBackdrop.ts — the title screen's stadium-night backdrop (M80), painted
 * procedurally into one canvas at mount time: a dark bowl of stands, glowing
 * floodlight banks with haze beams, a warm pitch-level horizon glow, a glossy
 * floor sheen for the trophy to stand on, and a heavy edge vignette.
 *
 * Zero downloads — and a swappable drop-in stays available: if
 * `public/title/bg.webp` exists (the owner's generated art), the screen layers
 * it OVER this canvas (img onerror simply removes it, the M63 pattern).
 * Deterministic (golden-angle scatter, no Math.random).
 */

const W = 1600;
const H = 900;

export function paintTitleBackdrop(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;

  // ---- Night base. ----
  const base = g.createLinearGradient(0, 0, 0, H);
  base.addColorStop(0, '#03040a');
  base.addColorStop(0.55, '#060912');
  base.addColorStop(0.72, '#080b15');
  base.addColorStop(1, '#04050c');
  g.fillStyle = base;
  g.fillRect(0, 0, W, H);

  // ---- Star specks, upper sky only. ----
  for (let i = 0; i < 90; i++) {
    const a = i * 2.39996;
    const x = ((Math.cos(a) * 0.5 + 0.5) * W + i * 37.3) % W;
    const y = (((Math.sin(a) * 0.5 + 0.5) * H) / 3 + i * 2.1) % (H * 0.34);
    g.fillStyle = `rgba(255,255,255,${0.12 + (i % 5) * 0.06})`;
    g.fillRect(x, y, i % 7 === 0 ? 2 : 1, i % 7 === 0 ? 2 : 1);
  }

  // ---- The bowl: concentric stand tiers wrapping behind the pitch. ----
  const tierY = H * 0.62; // pitch level — the stands rise above it
  for (let tier = 0; tier < 3; tier++) {
    const ry = 210 - tier * 52;
    g.strokeStyle = `rgba(26, 34, 56, ${0.5 - tier * 0.12})`;
    g.lineWidth = 34 - tier * 8;
    g.beginPath();
    g.ellipse(W / 2, tierY + 40, W * 0.66 - tier * 60, ry, 0, Math.PI, Math.PI * 2);
    g.stroke();
  }
  // Crowd speckle riding the tiers (tiny cool dots — distant heads/seats).
  for (let i = 0; i < 900; i++) {
    const a = i * 2.39996;
    const t = (i % 300) / 300;
    const tier = i % 3;
    const rx = W * 0.66 - tier * 60;
    const ry = 210 - tier * 52;
    const ang = Math.PI + t * Math.PI;
    const x = W / 2 + Math.cos(ang) * rx + Math.sin(a) * 12;
    const y = tierY + 40 + Math.sin(ang) * ry + Math.cos(a) * 9;
    if (y > tierY + 26) continue; // stands only — never speckle the pitch
    g.fillStyle = i % 9 === 0 ? 'rgba(200,214,255,0.28)' : 'rgba(90,110,160,0.16)';
    g.fillRect(x, y, 1.6, 1.6);
  }

  // ---- Floodlight banks + haze beams. ----
  const banks: [number, number, number][] = [
    [0.1, 0.2, 1],
    [0.33, 0.1, 0.8],
    [0.67, 0.1, 0.8],
    [0.9, 0.2, 1],
  ];
  for (const [fx, fy, str] of banks) {
    const x = fx * W;
    const y = fy * H;
    // The lamp cluster in a dark housing (naked dot-grids floated in the sky).
    g.fillStyle = '#0b101d';
    g.beginPath();
    g.roundRect(x - 32, y - 12, 64, 28, 4);
    g.fill();
    g.fillStyle = 'rgba(60,72,104,0.7)';
    g.fillRect(x - 3, y + 16, 6, 26); // the mast stub under the housing
    for (let r = 0; r < 2; r++)
      for (let i = 0; i < 5; i++) {
        g.fillStyle = 'rgba(255,244,214,0.95)';
        g.fillRect(x - 22 + i * 10, y - 6 + r * 10, 6, 6);
      }
    // Bloom.
    const bloom = g.createRadialGradient(x, y, 4, x, y, 150 * str);
    bloom.addColorStop(0, `rgba(255,235,190,${0.5 * str})`);
    bloom.addColorStop(0.35, `rgba(255,214,140,${0.16 * str})`);
    bloom.addColorStop(1, 'rgba(255,200,110,0)');
    g.fillStyle = bloom;
    g.fillRect(x - 160 * str, y - 160 * str, 320 * str, 320 * str);
    // A hazy beam falling toward the pitch centre.
    const cx = W / 2;
    g.save();
    const beam = g.createLinearGradient(x, y, cx, tierY + 60);
    beam.addColorStop(0, `rgba(255,236,196,${0.1 * str})`);
    beam.addColorStop(1, 'rgba(255,236,196,0)');
    g.fillStyle = beam;
    g.beginPath();
    g.moveTo(x - 14, y);
    g.lineTo(x + 14, y);
    g.lineTo(cx + (x < cx ? 220 : -60), tierY + 80);
    g.lineTo(cx + (x < cx ? 60 : -220), tierY + 80);
    g.closePath();
    g.fill();
    g.restore();
  }

  // ---- Warm pitch-level horizon glow (the reference's gold under-light). ----
  const horizon = g.createRadialGradient(W / 2, tierY + 30, 30, W / 2, tierY + 30, W * 0.62);
  horizon.addColorStop(0, 'rgba(255,196,96,0.14)');
  horizon.addColorStop(0.45, 'rgba(255,176,70,0.06)');
  horizon.addColorStop(1, 'rgba(255,160,50,0)');
  g.fillStyle = horizon;
  g.fillRect(0, tierY - 170, W, 320);

  // ---- The floor: glossy dark stage with a broad warm sheen. ----
  const floor = g.createLinearGradient(0, tierY, 0, H);
  floor.addColorStop(0, 'rgba(16,20,34,0.9)');
  floor.addColorStop(0.25, 'rgba(10,13,24,1)');
  floor.addColorStop(1, 'rgba(4,5,11,1)');
  g.fillStyle = floor;
  g.fillRect(0, tierY + 26, W, H - tierY);
  const sheen = g.createRadialGradient(W * 0.42, H * 0.9, 20, W * 0.42, H * 0.9, W * 0.5);
  sheen.addColorStop(0, 'rgba(255,206,120,0.1)');
  sheen.addColorStop(0.5, 'rgba(150,160,220,0.04)');
  sheen.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = sheen;
  g.fillRect(0, tierY, W, H - tierY);
  // Faint mirrored light streaks on the floor under the banks.
  for (const [fx, , str] of banks) {
    const x = fx * W;
    const streak = g.createLinearGradient(0, tierY + 30, 0, H * 0.94);
    streak.addColorStop(0, `rgba(255,226,170,${0.08 * str})`);
    streak.addColorStop(1, 'rgba(255,226,170,0)');
    g.fillStyle = streak;
    g.fillRect(x - 26, tierY + 30, 52, H);
  }

  // ---- Heavy edge vignette (the cinematic frame). ----
  const vig = g.createRadialGradient(W / 2, H * 0.55, H * 0.32, W / 2, H * 0.55, W * 0.72);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(0.72, 'rgba(2,3,8,0.42)');
  vig.addColorStop(1, 'rgba(1,2,6,0.9)');
  g.fillStyle = vig;
  g.fillRect(0, 0, W, H);

  return c;
}
